"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Facebook,
  Send,
  CheckCircle,
} from "lucide-react"

const contactInfo = [
  {
    icon: MapPin,
    title: "Adres",
    content: "ul. Działyńskich 12A",
    subcontent: "(nad Pocztą Polską)",
  },
  {
    icon: Phone,
    title: "Telefon",
    content: "508 391 393",
    href: "tel:+48508391393",
  },
  {
    icon: Mail,
    title: "E-mail",
    content: "kontakt@biurorzymska.pl",
    href: "mailto:kontakt@biurorzymska.pl",
  },
  {
    icon: Clock,
    title: "Godziny otwarcia",
    content: "Pon - Pt: 8:00 - 16:00",
    subcontent: "Sobota: po uzgodnieniu",
  },
]

export function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1000))
    setIsLoading(false)
    setIsSubmitted(true)
  }

  return (
    <section id="kontakt" className="py-20 md:py-32 bg-secondary/50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">
            Kontakt
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mt-4 mb-6 text-balance">
            Skontaktuj się z nami
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Masz pytania? Chętnie na nie odpowiem. Zadzwoń, napisz lub odwiedź 
            nasze biuro osobiście.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-2 space-y-6">
            {contactInfo.map((info, index) => (
              <Card key={index} className="bg-card border-border">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <info.icon className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">
                      {info.title}
                    </h4>
                    {info.href ? (
                      <a
                        href={info.href}
                        className="text-muted-foreground hover:text-accent transition-colors"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-muted-foreground">{info.content}</p>
                    )}
                    {info.subcontent && (
                      <p className="text-muted-foreground/70 text-sm">
                        {info.subcontent}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Social Links */}
            <Card className="bg-primary border-primary">
              <CardContent className="p-6">
                <h4 className="font-semibold text-primary-foreground mb-4">
                  Śledź nas w mediach społecznościowych
                </h4>
                <a
                  href="https://www.facebook.com/profile.php?id=100094100650389"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                  <span>Facebook</span>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="lg:col-span-3 bg-card border-border">
            <CardContent className="p-6 md:p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="font-serif text-2xl text-foreground mb-3">
                    Dziękujemy za wiadomość!
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Odpowiemy najszybciej jak to możliwe, zazwyczaj w ciągu 24 godzin.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                  >
                    Wyślij kolejną wiadomość
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-foreground">
                        Imię i nazwisko
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Jan Kowalski"
                        required
                        className="bg-background border-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-foreground">
                        Telefon
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="123 456 789"
                        className="bg-background border-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-foreground">
                      Adres e-mail
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jan@example.com"
                      required
                      className="bg-background border-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-foreground">
                      Temat
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      placeholder="W czym mogę pomóc?"
                      required
                      className="bg-background border-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-foreground">
                      Wiadomość
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Opisz swoją sprawę..."
                      rows={5}
                      required
                      className="bg-background border-input resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-6"
                  >
                    {isLoading ? (
                      "Wysyłanie..."
                    ) : (
                      <>
                        Wyślij wiadomość
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                  <p className="text-muted-foreground text-sm text-center">
                    Odpowiadamy na wiadomości zazwyczaj w ciągu 24 godzin.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}
